import axios from 'axios'
import { APIHELPER } from '@/constants'
import { sessionHelper } from '@/utils'
import store from '@/state/store'
import qs from 'qs'

export function apiHelper(baseURL, appendAuthHeader = true, checkTokenExpired = true) {
  let authHeader = {}
  let userType = sessionHelper.getUserType();
  let userToken = sessionHelper.getUserToken();
  let staffToken = sessionHelper.getStaffUserToken();
  let staffExpiredTime = sessionHelper.getStaffUserExpiredTime();
  let staffRefreshExpiredTime = sessionHelper.getStaffUserRefreshExpiredTime();
  let expiredTime = sessionHelper.getUserExpiredTime();
  let refreshExpiredTime = sessionHelper.getUserRefreshExpiredTime();

  // one_sales had two guards — staff (ADMIN) and customer (STAFF, its patient
  // portal). The inbox has only agents, and the customer routes went with the
  // CRM, so treat anything that is not explicitly STAFF as an agent.
  //
  // The default matters: with `== "ADMIN"` a missing USER_TYPE fell through to
  // the customer branch, read a token that is never set, and sent every request
  // without an Authorization header — which looks like "my session expired"
  // rather than like a bug.
  if(userType != "STAFF") {
    if (userToken && appendAuthHeader) {
      authHeader.Authorization = `Bearer ${userToken}`
    }
  }
  else {
    if (staffToken && appendAuthHeader) {
      authHeader.Authorization = `Bearer ${staffToken}`
    }
  }

  const apiInstance = axios.create({
    baseURL,
    paramsSerializer(params) {
      return qs.stringify(params, { arrayFormat: 'indices', encodeValuesOnly: true })
    }
  })

  async function checkExpired() {
    if(userType != "STAFF") {
      if(checkTokenExpired == true) {
        var tokenExpired = sessionHelper.isTokenExpired();
        if (tokenExpired == true) {
          var refreshTokenExpired = sessionHelper.isRefreshTokenExpired();
          if (refreshTokenExpired == true) {
           await store.dispatch('auth/logout');
          }
          else {
           await store.dispatch('auth/refreshToken');
          }
        }
      }
    }

    else {
      if(checkTokenExpired == true) {
        var staffTokenExpired = sessionHelper.isStaffTokenExpired();
        if (staffTokenExpired == true) {
          var staffRefreshTokenExpired = sessionHelper.isStaffRefreshTokenExpired();
          if (staffRefreshTokenExpired == true) {
           await store.dispatch('auth/staffLogout');
          }
          else {
           await store.dispatch('auth/staffRefreshToken');
          }
        }
      }
    }
    
  }

  const responseHandler = (response) => {
    return response.data
  }

  const errorHandler = async (error) => {
    var errorMessage = "";
    var errorReturnMessage = "";
    var errorType = "";
    var errorKey = "";
    var errorResponse = error.response;
    var errorStatus = 0;
    var errorData = null;
    if (!errorResponse) {
      errorMessage = error.message;
      var errorReturnCode = "";

      if (errorMessage === APIHELPER.NETWORK_ERROR) {
        errorReturnMessage = APIHELPER.NETWORK_ERROR;
        errorReturnCode = APIHELPER.NETWORK_ERROR_CODE;
      }
      else {
        errorReturnMessage = APIHELPER.UNKNOWN_ERROR;
        errorReturnCode = APIHELPER.UNKNOWN_ERROR_CODE;
      }
      // No HTTP response (CORS blocked / timeout / connection dropped):
      // synthesize a response in the same shape Laravel's Handler returns
      // ({ code, success, error }) so callers reject and read it as usual.
      error.response = {
        data: {
          code: errorReturnCode,
          success: false,
          error: errorReturnMessage,
          alertMessage: errorReturnMessage,
        },
      };
      throw error;
    }
    else {
      errorStatus = error.response.status;
      switch (errorStatus) {
        case 401:
          if(error.response.data.error) {
            errorReturnMessage = error.response.data.error
          }
          else {
            errorReturnMessage = APIHELPER.INVALID_USER;
          }
          await checkExpired();
          break;
        case 400:
          errorType = typeof error.response.data.error;
          errorData = error.response.data.error;
          if (errorType === APIHELPER.OBJECT) {
            console.log(error.response.data);
            Object.keys(errorData).map(
              (key) => {
                if (errorKey === '') {
                  errorKey = key;
                } else {
                  errorKey += ',' + key;
                }
              }
            );

            errorReturnMessage = errorKey + APIHELPER.OBJECT_INVALID;
          }
          else {
            errorReturnMessage = errorData;
          }
          break;
        default:
          errorReturnMessage = error.response.data.error;
      }
      error.response.data.alertMessage = errorReturnMessage;
      throw error
    }
  }


  apiInstance.interceptors.request.use(async (config) => {
    if(userType != "STAFF") {
      if (userToken) {
        if(!expiredTime && !refreshExpiredTime) {
          await store.dispatch('auth/logout');
        }
        await checkExpired();
      }
      userToken = sessionHelper.getUserToken(); 

      if (userToken && appendAuthHeader) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${userToken}`,
        }
      }
    }
    else {
      if (staffToken) {
        if(!staffExpiredTime && !staffRefreshExpiredTime) {
          await store.dispatch('auth/staffLogout');
        }
        await checkExpired();
      }
      staffToken = sessionHelper.getStaffUserToken();

      if (staffToken && appendAuthHeader) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${staffToken}`,
        }
      }
    }

    /**
     * X-Tenant-Id — which brand the signed-in person is currently working in.
     *
     * Added here, once, rather than in each service: a service that forgot it
     * would silently run unscoped for staff.
     *
     * It is NOT a security control. The server can only ever NARROW with it:
     * staff may select any brand, and anyone else may select one of the brands
     * on their own `tenant_user` rows — a header naming anything else is
     * discarded and leaves them scoped to their own. There is nothing here an
     * agent could tamper with to reach another brand.
     * See App\Support\TenantContext.
     *
     * A CALLER MAY SET IT ITSELF, and then this leaves it alone. One request
     * can legitimately be about a brand other than the one on screen — copying
     * a knowledge-base document from one brand to another is a read of A and a
     * write to B in the same breath — and without this the header from the
     * picker would scope both halves to whatever is selected and the write
     * would be refused. It is not a loophole: whatever is set here is still
     * run through the same narrowing on the server.
     */
    const selectedTenant = localStorage.getItem('inbox_tenant_id') // tenantHelper.key
    if (selectedTenant && !(config.headers && config.headers['X-Tenant-Id'])) {
      config.headers = {
        ...config.headers,
        'X-Tenant-Id': selectedTenant,
      }
    }

    return config
  })

  apiInstance.interceptors.response.use(
    (response) => responseHandler(response),
    (error) => errorHandler(error)
  )

  return apiInstance
}
